import { useCampaignCode } from '@campaign-services/hooks/useCampaignCode'
import { useFpCampaignProductsService } from '@cart-services/hooks/useFpCampaignProductsService'
import { type CountryCode, type ProjectCode } from '@constants'
import { useApiCampaignActivityInfo } from '@hooks-api/fortune/useApiCampaignActivityInfo'
import { Box } from '@pets-ui/Box/Box'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { VStack } from '@pets-ui/VStack/VStack'
import { format } from 'date-fns'
import { useTranslation } from 'react-i18next'
import ShepherdLogo from '../../assets/shepherdtech-logo.svg?react'
import { TheCampaignHeader } from '../../components/TheCampaignHeader'
import { type Brand } from '../../model/campaign'
import style from '../../style/pages/client-closed.module.scss'
import { loadCustomerServiceClientData } from '../client-data-import'

export interface ClientClosedProps {
  shouldHideHeader?: boolean
}

const ClientClosed = (props: ClientClosedProps) => {
  const { t } = useTranslation()
  const { shouldHideHeader } = props
  const { campaignCodeFromUrl } = useCampaignCode()
  const { destructuredData: exhibitorSettings } = useApiCampaignActivityInfo(campaignCodeFromUrl)
  const campaignId = exhibitorSettings?.campaign_id ?? undefined
  const { region: countryCode } = exhibitorSettings || {}
  const { projectCode } = useFpCampaignProductsService(campaignCodeFromUrl)
  const brands = exhibitorSettings?.brand_codes_info
  const startTime = exhibitorSettings?.start_time ? format(new Date(exhibitorSettings.start_time), 'yyyy/MM/dd') : ''
  const endTime = exhibitorSettings?.end_time ? format(new Date(exhibitorSettings.end_time), 'yyyy/MM/dd') : ''
  const country = exhibitorSettings?.region.toLowerCase() || 'hk'
  const hasDeadline = exhibitorSettings?.end_time !== null

  return (
    <main className={style.root}>
      <TheCampaignHeader projectCode={projectCode as ProjectCode} campaignId={campaignId as number} countryCode={countryCode as CountryCode} />
      <div className={style.content}>
        <VStack>
          <Text fontWeight={600} color="#4A5568">{exhibitorSettings?.title}</Text>
          {hasDeadline && (
            <Text variant="caption" fontWeight={600} color="#4A5568">
              {startTime}
              -
              {endTime}
            </Text>
          )}
        </VStack>
        <Box className={style.title}>
          <Text variant="subtitle" align="center">{t('global.eventNotStarted')}</Text>
        </Box>
        <div className={style.cards}>
          {Object.values(brands || {}).map((brand: Brand) => {
            return (
              <a href={brand.brand_url} key={brand.project_code} className={style.card}>
                <img className={style['card-img']} src={brand.brand_image} alt={brand.brand_name} />
              </a>
            )
          })}
        </div>
      </div>
      <div className={style.footer}>
        <VStack gap={2}>
          <VStack color={colors.grey500}>
            <Text>{t('copyright.company')}</Text>
            <Text>HONG KONG HUNG HOM KLN MAN YUE ST KAISER EST NO 41 RM 2 UNIT A2, 6F </Text>
            <Text>{loadCustomerServiceClientData(country)?.customerService || ''}</Text>
          </VStack>
          <VStack gap={2} alignItems="flex-start">
            <a href={shouldHideHeader ? `/campaigns/${campaignCodeFromUrl}/FAQ?hideheader=1` : `/campaigns/${campaignCodeFromUrl}/FAQ`} className={style.link}>{t('copyright.faq')}</a>
            <a href={shouldHideHeader ? `/campaigns/${campaignCodeFromUrl}/privacy?hideheader=1` : `/campaigns/${campaignCodeFromUrl}/privacy`} className={style.link}>{t('copyright.privacyPolicy')}</a>
            <Text color={colors.grey500}>
              Copyright © 2024 Shepherd Trading Limited., LTD. All rights reserved.
            </Text>
            <ShepherdLogo width={66} height={20} />
          </VStack>
        </VStack>
      </div>
    </main>
  )
}

export default ClientClosed
